import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import type { WatermarkSummary } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { watermarkOfferMessages as messages } from './watermarkOfferMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkGetsProps {
  summary: WatermarkSummary;
  /** `offer`: the three lines of b4. `takeover`: the lines of b3, with the scheduled posts. */
  variant: 'offer' | 'takeover';
}

const Check: React.FC = () => (
  <span className={styles.ok} aria-hidden="true">
    <FontAwesomeIcon icon={faCheck} />
  </span>
);

/**
 * What a plan gives (design: trial-upsell b3, b4 `.tu-gets`). The lines that promise clean copies
 * of the old files, and clean scheduled posts, show only while the licence can still get its one
 * run (`cleanCopiesAvailable`, spec §14.1).
 */
const WatermarkGetsComponent: React.FC<WatermarkGetsProps> = ({ summary, variant }) => {
  const intl = useIntl();
  const promisesCopies = summary.cleanCopiesAvailable && summary.files > 0;
  const isTakeover = variant === 'takeover';
  return (
    <ul className={styles.gets} data-testid="watermark-gets">
      {promisesCopies && (
        <li data-testid="watermark-gets-copies">
          <Check />
          <div>
            {intl.formatMessage(messages.getCopies, { count: summary.files })}
            <em>{intl.formatMessage(isTakeover ? messages.getCopiesNoteTakeover : messages.getCopiesNote)}</em>
          </div>
        </li>
      )}
      {isTakeover && promisesCopies && summary.scheduledPosts > 0 && (
        <li data-testid="watermark-gets-posts">
          <Check />
          <div>{intl.formatMessage(messages.getPosts, { count: summary.scheduledPosts })}</div>
        </li>
      )}
      {!isTakeover && (
        <li>
          <Check />
          <div>
            {intl.formatMessage(messages.getFullSize)}
            <em>{intl.formatMessage(messages.getFullSizeNote)}</em>
          </div>
        </li>
      )}
      <li>
        <Check />
        <div>{intl.formatMessage(isTakeover ? messages.getNewName : messages.getNew)}</div>
      </li>
    </ul>
  );
};

export const WatermarkGets = TypedMemo(WatermarkGetsComponent);
